import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react'

import { ActionInfoQuiet, Button, Highlight, ProductThumb, ScreenInfo, TextField } from '@krp/ui-kit'

import '../../../shared/platform.css'
import { thumbOf, type ProductPhotoUrl } from '../../../shared/product'
import { QueryState } from '../../../shared/QueryState'
import { PICK_PURPOSE_TEXT } from '../model/purposes'
import type { ProductPickerActions, ProductPickerVm } from '../model/types'
import './productPick.css'

/** Справка окна — единственная ⓘ, сверху слева. */
export const PRODUCT_PICK_INFO = {
  title: 'Выбор товара',
  lines: [
    'Это тот же список, что в «Номенклатуре»: найдите товар по названию, артикулу или штрихкоду.',
    'Нажмите «Выбрать» у нужного товара — дальше система спросит, точно ли поставить ему это фото.',
    'Ничего не записывается, пока вы не подтвердите.',
  ],
} as const

/**
 * Выбор товара общего списка (RP-FB56): режим «выбери товар» поверх «Общего каталога». Сверху — снимок, который хотят
 * прикрепить, и что на нём прочитано; поле поиска предзаполнено прочитанным названием (человек может стереть); строки
 * списка выбирают товар. Вёрстка от телефона: одна колонка, цели нажатия не меньше 44 px. Возврат один — «← назад» окна.
 */
export function ProductPickerScreen({
  vm,
  actions,
  photoUrl,
}: {
  vm: ProductPickerVm
  actions: ProductPickerActions
  photoUrl?: ProductPhotoUrl | undefined
}) {
  const list = vm.list
  const text = PICK_PURPOSE_TEXT[vm.purpose]
  return (
    <ActionInfoQuiet>
      <section className="platform-pick" aria-labelledby="platform-pick-title">
        <header className="platform-pick__head">
          <ScreenInfo title={PRODUCT_PICK_INFO.title} lines={PRODUCT_PICK_INFO.lines} />
          <h2 id="platform-pick-title">{text.title}</h2>
        </header>
        <p className="platform-pick__lead">{text.lead}</p>

        <div className="platform-pick__frame" role="group" aria-label="Снимок для прикрепления">
          {vm.framePreviewUrl === null ? (
            <span className="platform-pick__noframe" role="img" aria-label="Снимка нет">
              <ImageOff size={22} aria-hidden="true" />
            </span>
          ) : (
            <img src={vm.framePreviewUrl} alt="Снимок, который нужно прикрепить" />
          )}
          <p>
            {vm.frameLost
              ? 'Снимок потерян: страницу обновили. Вернитесь и снимите товар ещё раз.'
              : vm.readName === null
                ? 'Что на снимке прочитано — неизвестно. Найдите товар сами.'
                : `Прочитано: «${vm.readName}»`}
          </p>
        </div>

        <div className="platform-pick__search" role="search">
          <TextField
            type="search"
            label="Поиск товара"
            placeholder="Наименование, артикул или штрихкод"
            value={list.search}
            onChange={actions.searchChanged}
          />
        </div>

        <QueryState query={list.query} emptyText={list.emptyText} onRetry={actions.reload} />

        {list.query.status === 'ready' && (
          <>
            <p className="platform-pick__count" aria-live="polite">
              {list.rangeText}
              {list.query.refreshing && ' · обновляется…'}
            </p>
            <ul className="platform-pick__list" aria-label="Товары общего каталога">
              {list.rows.map((row) => (
                <li key={row.id} className="platform-pick__row">
                  <ProductThumb src={thumbOf(row.commonPhotoUrl, row.refs, photoUrl)} name={row.name} size={48} />
                  <div className="platform-pick__text">
                    <p className="platform-pick__name">
                      <Highlight text={row.name} query={list.search} />
                    </p>
                    <p className="platform-pick__meta">
                      Артикул {row.sku ?? '—'}
                      {row.barcodeCount > 0 ? ` · штрихкодов: ${row.barcodeCount}` : ''}
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    aria-label={`Выбрать товар ${row.name}`}
                    onClick={() => actions.pick(row)}
                    actionId="platform.product-pick.pick"
                  >
                    Выбрать
                  </Button>
                </li>
              ))}
            </ul>
            {list.pages > 1 && (
              <nav className="platform-pick__pager" aria-label="Страницы списка">
                <Button
                  variant="secondary"
                  icon={<ChevronLeft size={16} aria-hidden="true" />}
                  disabled={!list.canPrevious}
                  onClick={() => actions.pageChanged(list.page - 1)}
                  actionId="platform.product-pick.page"
                  disabledReason={list.canPrevious ? null : 'Это первая страница.'}
                >
                  Назад
                </Button>
                <span aria-current="page">
                  Страница {list.page} из {list.pages}
                </span>
                <Button
                  variant="secondary"
                  icon={<ChevronRight size={16} aria-hidden="true" />}
                  disabled={!list.canNext}
                  onClick={() => actions.pageChanged(list.page + 1)}
                  actionId="platform.product-pick.page"
                  disabledReason={list.canNext ? null : 'Это последняя страница.'}
                >
                  Дальше
                </Button>
              </nav>
            )}
          </>
        )}

        <footer className="platform-pick__bar">
          <Button onClick={actions.close} actionId="platform.product-pick.close">
            Отмена
          </Button>
        </footer>
      </section>
    </ActionInfoQuiet>
  )
}
